import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, FileText, FileSpreadsheet, Printer, ShieldCheck, ArrowLeft, CheckCircle2, Building2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const isReport = (d) => d && typeof d === 'object' && d.supplier && d.summary && Array.isArray(d.rows);
const SITE = window.location.origin;
const money = (v) => Number(v || 0).toFixed(2);

export const reportParams = (f, overrides = {}) => {
  const p = {
    channel: f.channel || 'all', payment_status: f.payment_status || 'all',
    purchase_from: f.purchase_from || '', purchase_to: f.purchase_to || '',
    visit_from: f.date_from || '', visit_to: f.date_to || '', rate_name: f.rate_name || '',
    site_origin: SITE, ...overrides,
  };
  return Object.fromEntries(Object.entries(p).filter(([, v]) => v !== ''));
};

export const downloadReport = async (fmt, params, slug = 'sales') => {
  try {
    const res = await axios.get(`${API}/api/eticket/verification-report/${fmt}`, { params, responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url; a.download = `${slug}-sales-verification.${fmt}`; a.click();
    URL.revokeObjectURL(url);
  } catch { toast.error('Export failed.'); }
};

function Card({ label, value, testid, accent }) {
  return (
    <div className="rounded-xl bg-slate-50 px-4 py-3" data-testid={testid}>
      <p className="text-[11px] uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`text-xl font-bold ${accent || 'text-[#0C2159]'}`}>{value}</p>
    </div>
  );
}

export function SummaryCards({ s }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="verification-summary-cards">
      <Card label="Total paid bookings" value={s.paid_bookings} testid="card-paid-bookings" />
      <Card label="Total tickets sold" value={s.tickets_sold} testid="card-tickets-sold" />
      <Card label="Gross paid sales" value={`${s.currency} ${money(s.gross_paid)}`} testid="card-gross" />
      <Card label="Refunds" value={`${s.currency} ${money(s.refunds)}`} testid="card-refunds" accent="text-red-600" />
      <Card label="Net sales" value={`${s.currency} ${money(s.net)}`} testid="card-net" accent="text-emerald-700" />
    </div>
  );
}

export function SupplierGrid({ sup, period, generated }) {
  const items = [
    ['Attraction', sup.attraction_name],
    ['Technology provider', sup.technology_provider], ['Ticketing platform', sup.ticketing_platform],
    ['Official booking website', sup.official_booking_url], ['Reporting period', period], ['Generated', generated],
  ];
  return (
    <dl className="grid sm:grid-cols-2 lg:grid-cols-6 gap-x-5 gap-y-3 text-sm" data-testid="supplier-info-panel">
      {items.map(([k, v]) => (
        <div key={k} className="min-w-0">
          <dt className="text-[11px] uppercase tracking-wide text-slate-400">{k}</dt>
          <dd className={`font-medium break-words ${v === 'Not configured' ? 'text-amber-600 italic' : 'text-slate-800'}`}>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

const periodText = (f) => `${f.purchase_from || 'Earliest record'} → ${f.purchase_to || 'latest record'}`;

export function VerificationPanel({ filters, reloadKey }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  useEffect(() => {
    setData(null);
    axios.get(`${API}/api/eticket/verification-report`, { params: reportParams(filters) })
      .then(r => setData(isReport(r.data) ? r.data : false)).catch(() => setData(false));
  }, [filters, reloadKey]);
  const csv = async () => { setBusy('csv'); await downloadReport('csv', reportParams(filters), data.workspace.slug); setBusy(''); };
  if (data === false) return null;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-4" data-testid="verification-panel">
      {!data ? <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div> : (
        <>
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-cyan-700 font-semibold flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> {data.report_type}</p>
              <h2 className="text-lg font-bold text-[#0C2159]" data-testid="verification-title">{data.supplier.attraction_name} Online Ticket Sales Report</h2>
              <p className="text-xs text-slate-500">Online ticket booking and sales records for supplier verification and commercial due diligence.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={csv} disabled={busy === 'csv'} data-testid="export-csv-btn" className="inline-flex items-center gap-2 border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-medium px-3.5 py-2 rounded-xl disabled:opacity-60">
                {busy === 'csv' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />} Export CSV
              </button>
            </div>
          </div>
          <SupplierGrid sup={data.supplier} period={periodText(data.filters)} generated={data.generated_at} />
          <div className="mt-4"><SummaryCards s={data.summary} /></div>
          <p className="text-[11px] text-slate-400 mt-2" data-testid="verification-footnote">
            Cards follow the filters above. Paid = successfully paid (incl. later refunded). {data.summary.demo_excluded ? `${data.summary.demo_excluded} demo/test record(s) excluded. ` : ''}
            Reconciliation: not performed.
          </p>
        </>
      )}
    </div>
  );
}

const STATUS_LABEL = { all: 'All', paid: 'Paid', pending: 'Pending', failed: 'Failed', refunded: 'Refunded', cancelled: 'Cancelled' };
const CHANNEL_LABEL = { all: 'All channels', direct: 'Own Website Booking', bokun: 'Bókun', globaltix: 'GlobalTix' };

export function ActiveFilterSummary({ company, f }) {
  const parts = [`Attraction: ${company?.supplier_info?.attraction_name || company?.name || '—'}`,
    `Channel: ${CHANNEL_LABEL[f.channel] || f.channel}`, `Payment Status: ${STATUS_LABEL[f.payment_status] || f.payment_status}`];
  if (f.rate_name) parts.push(`Type: ${f.rate_name}`);
  if (f.purchase_from || f.purchase_to) parts.push(`Purchased: ${f.purchase_from || '…'} → ${f.purchase_to || '…'}`);
  if (f.date_from || f.date_to) parts.push(`Visit: ${f.date_from || '…'} → ${f.date_to || '…'}`);
  return <p className="text-xs text-slate-500 mb-3" data-testid="active-filter-summary">{parts.join('  |  ')}</p>;
}

/* ---------- Settings: supplier information ---------- */
const SUP_FIELDS = [
  ['attraction_name', 'Attraction name (e.g. Indian Heritage Cultural Centre (IHCC), Penang)'],
  ['official_booking_url', 'Official booking website (blank = your StayMii booking page)'],
];

export function SupplierInfoSettings({ company, onSaved }) {
  const [f, setF] = useState(() => Object.fromEntries(SUP_FIELDS.map(([k]) => [k, company.supplier_info?.[k] || ''])));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await axios.put(`${API}/api/eticket/company/supplier-info`, f); toast.success('Supplier information saved.'); onSaved && onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Save failed (owner only).'); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="supplier-info-settings">
      <h3 className="font-bold text-slate-900 mb-1 flex items-center gap-2"><Building2 className="w-4 h-4" /> Supplier Information (Sales Verification Report)</h3>
      <p className="text-xs text-slate-500 mb-4">Shown on the sales verification report and exports. Technology provider is fixed: Mii Global Partner Holdings Sdn. Bhd.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        {SUP_FIELDS.map(([k, label]) => (
          <div key={k} className={k === 'attraction_name' || k === 'official_booking_url' ? 'sm:col-span-2' : ''}>
            <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
            <input value={f[k]} onChange={e => setF({ ...f, [k]: e.target.value })} data-testid={`supplier-${k}`}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
          </div>
        ))}
      </div>
      <button onClick={save} disabled={busy} data-testid="supplier-save" className="mt-4 bg-[#0C2159] hover:bg-[#13317a] text-white font-semibold px-5 py-2.5 rounded-xl flex items-center gap-2 disabled:opacity-60">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Save supplier information
      </button>
    </div>
  );
}

/* ---------- Print / review page ---------- */
export default function EticketReportPreview() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const params = Object.fromEntries(sp.entries());
  useEffect(() => {
    axios.get(`${API}/api/eticket/verification-report`, { params }).then(r => setData(isReport(r.data) ? r.data : false)).catch(() => setData(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp]);
  if (data === false) return <p className="p-10 text-center text-slate-500">Could not load report.</p>;
  if (!data) return <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  const s = data.summary;
  const basis = Object.entries(s.payment_basis).map(([k, v]) => `${k}: ${v}`).join('; ') || '—';
  return (
    <div className="min-h-screen bg-slate-100 print:bg-white" data-testid="report-preview-page">
      <style>{`@media print { @page { size: A4 landscape; margin: 12mm; } .no-print { display: none !important; } body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }`}</style>
      <div className="no-print sticky top-0 z-10 bg-[#0C2159] text-white px-6 py-3 flex flex-wrap items-center gap-3">
        <button onClick={() => nav('/eticket/admin')} className="flex items-center gap-1.5 text-sm text-cyan-200 hover:text-white" data-testid="report-back"><ArrowLeft className="w-4 h-4" /> Back to admin</button>
        <span className="text-sm font-semibold">Review report before downloading</span>
        <div className="flex-1" />
        <button onClick={() => window.print()} data-testid="report-print" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-sm px-3.5 py-2 rounded-lg"><Printer className="w-4 h-4" /> Print</button>
        <button onClick={() => downloadReport('csv', params, data.workspace.slug)} data-testid="report-download-csv" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-sm px-3.5 py-2 rounded-lg"><FileSpreadsheet className="w-4 h-4" /> CSV</button>
        <button onClick={() => downloadReport('pdf', params, data.workspace.slug)} data-testid="report-download-pdf" className="inline-flex items-center gap-2 bg-cyan-400 text-[#0C2159] font-semibold text-sm px-4 py-2 rounded-lg hover:bg-cyan-300"><FileText className="w-4 h-4" /> Download PDF</button>
      </div>
      <div className="max-w-[1180px] mx-auto bg-white my-6 print:my-0 p-8 print:p-0 shadow-sm print:shadow-none">
        <p className="text-[11px] uppercase tracking-[0.2em] text-cyan-700 font-semibold">{data.report_type} · StayMii E-Ticket</p>
        <h1 className="text-2xl font-bold text-[#0C2159] mt-1" data-testid="report-title">{data.supplier.attraction_name} Online Ticket Sales Report</h1>
        <p className="text-sm text-slate-500 mb-6">Online ticket booking and sales records for supplier verification and commercial due diligence.</p>
        <h3 className="text-xs font-bold uppercase tracking-wide text-[#0C2159] mb-2">A. Report header</h3>
        <SupplierGrid sup={data.supplier} period={periodText(data.filters)} generated={data.generated_at} />
        <p className="text-xs text-slate-500 mt-2">Sales channel: {data.filters.channel} · Payment status: {data.filters.payment_status} · Ticket type: {data.filters.ticket_type}</p>
        <h3 className="text-xs font-bold uppercase tracking-wide text-[#0C2159] mt-6 mb-2">B. Sales summary</h3>
        <SummaryCards s={s} />
        <h3 className="text-xs font-bold uppercase tracking-wide text-[#0C2159] mt-6 mb-2">C. Transaction records ({data.rows.length})</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-[11px] border border-slate-200" data-testid="report-table">
            <thead className="bg-[#0C2159] text-white">
              <tr>{['Booking ID', 'Purchase', 'Product', 'Category', 'Qty', 'Gross', 'Refund', 'Net', 'Cur', 'Payment', 'QR', 'Visit', 'Channel', 'Payment ref', 'Payment basis'].map(h => <th key={h} className="text-left px-2 py-1.5 font-semibold">{h}</th>)}</tr>
            </thead>
            <tbody>
              {data.rows.length === 0 ? <tr><td colSpan={15} className="p-4 text-center text-slate-400">No records match the selected filters.</td></tr> :
                data.rows.map((r, i) => (
                  <tr key={r.ticket_number + i} className="border-t border-slate-100 even:bg-slate-50 break-inside-avoid">
                    <td className="px-2 py-1.5 font-mono">{r.booking_id}</td><td className="px-2 py-1.5">{r.purchase_date}</td>
                    <td className="px-2 py-1.5">{r.product}</td><td className="px-2 py-1.5">{r.category}</td><td className="px-2 py-1.5">{r.quantity}</td>
                    <td className="px-2 py-1.5">{money(r.gross)}</td><td className="px-2 py-1.5">{money(r.refund)}</td><td className="px-2 py-1.5 font-semibold">{money(r.net)}</td>
                    <td className="px-2 py-1.5">{r.currency}</td><td className="px-2 py-1.5 capitalize">{r.payment_status}</td><td className="px-2 py-1.5">{r.qr_status}</td>
                    <td className="px-2 py-1.5">{r.visit_date}</td><td className="px-2 py-1.5">{r.channel}</td>
                    <td className="px-2 py-1.5 font-mono break-all max-w-[160px]">{r.payment_reference || '—'}</td><td className="px-2 py-1.5">{r.payment_basis}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <h3 className="text-xs font-bold uppercase tracking-wide text-[#0C2159] mt-6 mb-2">D. Verification notes</h3>
        <ul className="text-xs text-slate-600 space-y-1 list-disc pl-5" data-testid="report-notes">
          <li>Report generated from StayMii E-Ticket records for workspace “{data.workspace.name}”. Only this workspace's records are included.</li>
          {s.demo_excluded ? <li>{s.demo_excluded} record(s) flagged as demo/test data were excluded.</li> : null}
          <li>Selected channel: {data.filters.channel}. Reporting period: {periodText(data.filters)} (purchase date, MYT).</li>
          <li>Payment basis of paid records — {basis}.</li>
          <li>Reconciliation status: {data.reconciliation_status}</li>
          <li>Visitor names, emails and phone numbers are intentionally excluded. External supporting documents are provided separately and are not part of this system-generated report.</li>
        </ul>
        <p className="text-[10px] text-slate-400 mt-6">{data.title} · Generated {data.generated_at} · StayMii E-Ticket</p>
      </div>
    </div>
  );
}
